import { Check, CirclePlus } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import { cn } from "~/lib/utils";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import React from "react";
import { IconTypes } from "~/components/Icon/types";
import { Icon, isValidIconType } from "~/components/Icon";

// // // //

function OptionIcon(props: {
    icon: string | IconTypes | null | undefined;
    border?: boolean;
}) {
    const { icon, border = false } = props;

    if (icon === null || icon === undefined) {
        return (
            <ImageLoader
                src={RWA_COIN_ICON}
                alt=""
                className="h-3 w-3 object-cover rounded-full"
            />
        );
    }

    if (isValidIconType(icon)) {
        return <Icon icon={icon} className="text-foreground max-h-3.5" />;
    }

    return (
        <ImageLoader
            src={icon}
            alt=""
            className={cn("h-3 w-3 object-cover rounded-full", {
                "ring ring-slate-300": border,
            })}
            fallbackSrc={RWA_COIN_ICON}
        />
    );
}

function ValueDisplay(props: {
    value: string[];
    options: MultiSelectOption[];
    hasAnyIcon: boolean;
    maxBadgesShown: number;
    textSize: "sm" | "xs";
}) {
    const { value, options, hasAnyIcon, maxBadgesShown, textSize } = props;

    const shownValues = value.slice(0, maxBadgesShown);
    const moreValues = value.length - shownValues.length;
    const showMore = moreValues > 0;
    const moreLabel = showMore ? `+${moreValues} more` : null;

    if (maxBadgesShown === 0) {
        return (
            <span
                className={cn("font-normal text-secondary-foreground", {
                    "text-sm": textSize === "sm",
                    "text-xs": textSize === "xs",
                })}
            >
                {value.length} selected
            </span>
        );
    }

    return (
        <div className="flex flex-wrap gap-1">
            {shownValues.map((v) => {
                const option = options.find((o) => o.value === v);
                if (!option) return null;

                return (
                    <Badge
                        key={v}
                        variant="secondary"
                        className={cn(
                            "flex items-center gap-1 rounded-md border border-slate-300/80 px-1.5 py-0.5 font-normal",
                            {
                                "text-sm": textSize === "sm",
                                "text-xs": textSize === "xs",
                            },
                        )}
                    >
                        {hasAnyIcon && <OptionIcon icon={option.icon} />}
                        {option.label}
                    </Badge>
                );
            })}
            {showMore && (
                <Badge
                    variant="secondary"
                    className={cn(
                        "flex items-center gap-1 rounded-md border border-slate-300/80 px-1.5 py-0.5 font-normal",
                        {
                            "text-sm": textSize === "sm",
                            "text-xs": textSize === "xs",
                        },
                    )}
                >
                    {moreLabel}
                </Badge>
            )}
        </div>
    );
}

export interface MultiSelectOption {
    value: string;
    label: string;
    count?: number;
    icon?: string | IconTypes | null;
}

interface Props {
    options: MultiSelectOption[];
    value: string[];
    onChange: (value: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    maxBadgesShown?: number;
    className?: string;
    popoverWidth?: string;
    maxHeight?: string;
    searchEnabled?: boolean;
    searchPlaceholder?: string;
    /** Footer actions to select every option or clear selection (default true). */
    showBulkSelectionActions?: boolean;
    textSize?: "sm" | "xs";
}

/**
 * A generic multi-select component with a popover interface.
 * Displays selected items as badges and shows counts for each option.
 */
export function MultiSelect(props: Props) {
    const {
        options,
        value,
        onChange,
        disabled = false,
        placeholder = "Select options",
        maxBadgesShown = 2,
        className,
        popoverWidth = "w-[280px]",
        maxHeight = "max-h-[360px]",
        searchEnabled = false,
        searchPlaceholder = "Search...",
        showBulkSelectionActions = true,
        textSize = "sm",
    } = props;

    const handleToggle = (optionValue: string) => {
        if (disabled) {
            return;
        }

        const newValue = value.includes(optionValue)
            ? value.filter((v) => v !== optionValue)
            : [...value, optionValue];
        onChange(newValue);
    };

    const handleSelectAll = () => {
        if (disabled || options.length === 0) {
            return;
        }

        onChange(options.map((option) => option.value));
    };

    const handleClearAll = () => {
        if (disabled) {
            return;
        }

        onChange([]);
    };

    const allOptionsSelected =
        options.length > 0 &&
        options.every((option) => value.includes(option.value));

    const hasAnyIcon = options.some((opt) => opt.icon);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        "h-9 min-w-0 w-full justify-start gap-1.5 rounded-md bg-card px-3 font-normal shadow-xs hover:bg-slate-100",
                        {
                            "text-sm": textSize === "sm",
                            "text-xs": textSize === "xs",
                        },
                        "disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-100 disabled:hover:bg-slate-100 select-none",
                        className,
                    )}
                >
                    {value.length === 0 ? (
                        <>
                            <CirclePlus className="h-4 w-4 text-muted-foreground" />
                            <span className="font-normal">{placeholder}</span>
                        </>
                    ) : (
                        <ValueDisplay
                            value={value}
                            options={options}
                            hasAnyIcon={hasAnyIcon}
                            maxBadgesShown={maxBadgesShown}
                            textSize={textSize}
                        />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className={cn(popoverWidth, "p-0 rounded-lg shadow-xs z-500", {
                    "text-sm": textSize === "sm",
                    "text-xs": textSize === "xs",
                })}
                align="start"
            >
                <Command
                    className={cn({
                        "text-sm": textSize === "sm",
                        "text-xs": textSize === "xs",
                    })}
                >
                    {searchEnabled && (
                        <CommandInput
                            placeholder={searchPlaceholder}
                            className={cn(
                                "border-none focus:outline-none focus:ring-0 focus-visible:ring-0",
                                {
                                    "text-sm": textSize === "sm",
                                    "text-xs": textSize === "xs",
                                },
                            )}
                        />
                    )}
                    <CommandList className={maxHeight}>
                        {searchEnabled && (
                            <CommandEmpty>No results found.</CommandEmpty>
                        )}
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    onSelect={() => handleToggle(option.value)}
                                    disabled={disabled}
                                    className={cn(
                                        "text-slate-700 data-[selected='true']:bg-slate-100 data-[selected='true']:text-slate-900 data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900",
                                        {
                                            "text-sm": textSize === "sm",
                                            "text-xs": textSize === "xs",
                                        },
                                    )}
                                >
                                    <div
                                        className={cn(
                                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                            value.includes(option.value)
                                                ? "bg-primary text-primary-foreground"
                                                : "opacity-50",
                                        )}
                                    >
                                        {value.includes(option.value) && (
                                            <Check className="h-3 w-3" />
                                        )}
                                    </div>
                                    {hasAnyIcon && (
                                        <OptionIcon icon={option.icon} border />
                                    )}
                                    <span className="flex-1 truncate">
                                        {option.label}
                                    </span>
                                    {option.count !== undefined && (
                                        <span className="ml-auto rounded-md bg-slate-200 px-1.5 py-0.5 text-xs text-slate-500">
                                            {option.count}
                                        </span>
                                    )}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    {showBulkSelectionActions && options.length > 0 && (
                        <div
                            className="flex shrink-0 items-center justify-between gap-2 border-t px-2 py-2"
                            role="group"
                            aria-label="Bulk selection"
                        >
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={disabled || allOptionsSelected}
                                className="h-8 px-2 text-xs font-normal text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleSelectAll();
                                }}
                            >
                                Select all
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={disabled || value.length === 0}
                                className="h-8 px-2 text-xs font-normal text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleClearAll();
                                }}
                            >
                                Clear all
                            </Button>
                        </div>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
